import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatRand, resolveImageUrl } from "../lib/api";
import { useCart } from "../lib/cart";

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    line1: "",
    line2: "",
    city: "",
    province: "",
    postal_code: "",
    country: "South Africa",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const empty = items.length === 0;
  const total = useMemo(() => subtotal, [subtotal]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (empty) return;
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        notes: form.notes,
        shipping: {
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          province: form.province,
          postal_code: form.postal_code,
          country: form.country,
        },
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          image: i.image,
          price: i.price,
          quantity: i.quantity,
          size: i.size,
          color: i.color,
        })),
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success("Order placed.");
      navigate(`/order/${data.order.order_number}?email=${encodeURIComponent(data.order.customer_email)}&yoco=${encodeURIComponent(data.yoco_payment_link)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (empty) {
    return (
      <div className="section-pad py-24 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-4xl uppercase tracking-tighter">Cart is empty.</h1>
      </div>
    );
  }

  return (
    <div className="section-pad py-12 grid md:grid-cols-2 gap-16" data-testid="checkout-page">
      <form onSubmit={submit} className="space-y-8" data-testid="checkout-form">
        <h1 className="font-display text-4xl uppercase tracking-tighter mb-8">Checkout</h1>
        <div className="grid gap-5">
          <input required placeholder="Full name" className="input-box" value={form.customer_name} onChange={update("customer_name")} data-testid="checkout-name" />
          <input required type="email" placeholder="Email" className="input-box" value={form.customer_email} onChange={update("customer_email")} data-testid="checkout-email" />
          <input placeholder="Phone" className="input-box" value={form.customer_phone} onChange={update("customer_phone")} data-testid="checkout-phone" />
          <input required placeholder="Address" className="input-box" value={form.line1} onChange={update("line1")} data-testid="checkout-line1" />
          <input placeholder="Apartment, suite" className="input-box" value={form.line2} onChange={update("line2")} data-testid="checkout-line2" />
          <div className="grid grid-cols-2 gap-5">
            <input required placeholder="City" className="input-box" value={form.city} onChange={update("city")} data-testid="checkout-city" />
            <input required placeholder="Province" className="input-box" value={form.province} onChange={update("province")} data-testid="checkout-province" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <input required placeholder="Postal code" className="input-box" value={form.postal_code} onChange={update("postal_code")} data-testid="checkout-postal" />
            <input placeholder="Country" className="input-box" value={form.country} onChange={update("country")} data-testid="checkout-country" />
          </div>
        </div>
        <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="checkout-submit">
          {submitting ? "Placing..." : `Place Order · ${formatRand(total)}`}
        </button>
      </form>

      <aside className="md:pl-10" data-testid="checkout-summary">
        <div className="border hairline" style={{ background: "#fff" }}>
          <ul>
            {items.map((it, idx) => (
              <li key={idx} className="px-6 py-5 border-b hairline flex gap-4">
                <div className="w-14 h-16" style={{ background: "var(--bg-alt)" }}>
                  <img src={resolveImageUrl(it.image)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm uppercase">{it.name}</div>
                  <div className="text-xs mt-1 text-black/55">{[it.color, it.size].filter(Boolean).join(" · ")} · Qty {it.quantity}</div>
                </div>
                <div className="font-display text-sm whitespace-nowrap">{formatRand(it.price * it.quantity)}</div>
              </li>
            ))}
          </ul>
          <div className="px-6 py-5 flex items-center justify-between">
            <span className="text-sm">Total</span>
            <span className="font-display text-2xl">{formatRand(total)}</span>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Checkout;
